import { useEffect, useRef, useState } from 'react';
import { EXAM_DURATION_MS } from '@fluent/types';

/**
 * The exam clock. It runs only while the test screen is mounted AND the
 * page is visible — so closing the app, locking the phone or switching to
 * another app stops it, and it picks up from the same second on return
 * (Tornike 2026-09-25: no pause button, but closing the app pauses).
 *
 * Elapsed time is handed to `onPersist` every 2 s and whenever the page
 * hides or the screen unmounts, so an app killed mid-test loses at most
 * two seconds.
 */

const TICK_MS = 250;
const PERSIST_EVERY_MS = 2_000;

export interface ExamClockOptions {
  initialElapsedMs: number;
  running: boolean;
  onPersist: (elapsedMs: number) => void;
  onTimeUp: (elapsedMs: number) => void;
}

export function useExamClock({ initialElapsedMs, running, onPersist, onTimeUp }: ExamClockOptions): number {
  const [elapsed, setElapsed] = useState(initialElapsedMs);
  const baseRef = useRef(initialElapsedMs);
  const resumedAtRef = useRef<number | null>(null);
  const firedRef = useRef(false);
  const persistRef = useRef(onPersist);
  const timeUpRef = useRef(onTimeUp);
  persistRef.current = onPersist;
  timeUpRef.current = onTimeUp;

  useEffect(() => {
    if (!running) return;

    const read = () =>
      baseRef.current + (resumedAtRef.current === null ? 0 : Date.now() - resumedAtRef.current);

    const resume = () => {
      if (resumedAtRef.current === null && document.visibilityState === 'visible') {
        resumedAtRef.current = Date.now();
      }
    };
    const halt = () => {
      if (resumedAtRef.current !== null) {
        baseRef.current = read();
        resumedAtRef.current = null;
      }
      persistRef.current(baseRef.current);
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? resume() : halt());

    resume();
    let lastPersist = Date.now();
    const id = window.setInterval(() => {
      const now = read();
      setElapsed(now);
      if (now >= EXAM_DURATION_MS && !firedRef.current) {
        firedRef.current = true;
        halt();
        timeUpRef.current(now);
        return;
      }
      if (Date.now() - lastPersist >= PERSIST_EVERY_MS) {
        lastPersist = Date.now();
        persistRef.current(now);
      }
    }, TICK_MS);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', halt);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', halt);
      halt();
    };
  }, [running]);

  return elapsed;
}
